import { useState } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { IconButton, Tooltip } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { getJournalUsed, getJournalMax, getEntries, getEntryMax } from './AccountTab/accountHelpers';
import SnippetCopy from './SnippetCopy';
import { ClerkUserProfileSkin } from '../../components/AppBarComponents/ClerkSkins';
import { useUser } from '../../contexts/UserContext';
import SettingsRow from './SettingsTab/SettingsRow';
import SettingsSection from './SettingsTab/SettingsSection';
import SettingsSearch from './SettingsTab/SettingsSearch';
import { tooltipSx } from '../../components/shared/jernyTooltipStyling';
import config from '../../config.json';
import './AccountTab/AccountTab.css';

const _targetMcpUrl = config.targetMcpUrl;

/**
 * QuotaRow
 * Renders a single quota entry as a SettingsRow-style row with progress bar.
 *
 * @param {string} label
 * @param {number} used
 * @param {number|null} max
 */
function QuotaRow({ label, used, max }) {
    const pct = max ? Math.min((used / max) * 100, 100) : 0;
    const isWarn = pct > 80;
    const displayMax = max >= 2147483647 ? '∞' : max;

    return (
        <div className="account-quota">
            <span className="settings-row-label">{label}</span>
            <div>
                <div className="account-quota-numbers">{max ? `${used} / ${displayMax}` : used}</div>
                {max && (
                    <div className="account-quota-bar-track">
                        <div
                            className={`account-quota-bar-fill${isWarn ? ' account-quota-bar-fill--warn' : ''}`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * AccountTab
 * Account info in SettingsTab style — same font, same row pattern, collapsible sections.
 */
export default function AccountTab() {
    const { openUserProfile } = useClerk();
    const user = useUser();
    const [showApiKey, setShowApiKey] = useState(false);
    const [search, setSearch] = useState('');
    // Must be declared before any early return — Rules of Hooks
    const [copiedApiKey, setCopiedApiKey] = useState(false);

    // Filter helper — same pattern as SettingsTab
    const matches = (label) => label.toLowerCase().includes(search.toLowerCase());
    const anyMatch = (...labels) => !search || labels.some(matches);

    // User not yet loaded — wait for onUserLoaded event
    if (!user) return null;

    const tier = user?.tier ?? 'free';
    const isFree = tier === 'free';

    const handleUpdateProfile = () => {
        openUserProfile(ClerkUserProfileSkin);
    };

    const handleUpgrade = () => {
        window.location.href = config.upgradeUrl;
    };

    const handleCopyApiKey = () => {
        navigator.clipboard.writeText(user?.apiKey ?? '');
        // Brief delay before showing Copied feedback
        setTimeout(() => {
            setCopiedApiKey(true);
            setTimeout(() => setCopiedApiKey(false), 1500);
        }, 300);
    };

    const handleToggleApiKey = () => {
        setShowApiKey((v) => !v);
    };

    return (
        <div className="account-root">
            <div className="account-center">
                <div className="account-sticky">
                    <SettingsSearch value={search} onChange={setSearch} />
                </div>
                <div className="account-scroll">
                    {/* Plan & Usage section */}
                    {anyMatch('Plan', 'Upgrade') && (
                        <SettingsSection title="Plan & Usage">
                            {(matches('Plan') || !search) && (
                                <SettingsRow label="Plan">
                                    <span className="account-tier">{tier}</span>
                                    <span className="account-caption">{isFree ? '— Free forever' : '— €9.99 / month, thank you 🙏'}</span>
                                </SettingsRow>
                            )}
                            {isFree && (matches('Upgrade') || !search) && (
                                <SettingsRow label="Upgrade">
                                    <button className="account-upgrade-btn" onClick={handleUpgrade}>
                                        <DiamondOutlinedIcon style={{ fontSize: 12, marginRight: 4, verticalAlign: 'middle' }} />
                                        Upgrade to Pro — €9.99/mo
                                    </button>
                                </SettingsRow>
                            )}
                        </SettingsSection>
                    )}

                    {/* Quota section */}
                    {anyMatch('Entries', 'Topics') && (
                        <SettingsSection title="Usage">
                            {(matches('Entries') || !search) && (
                                <QuotaRow label="Entries" used={getEntries(user)} max={getEntryMax(user)} />
                            )}
                            {(matches('Topics') || !search) && (
                                <QuotaRow label="Topics" used={getJournalUsed(user)} max={getJournalMax(user)} />
                            )}
                        </SettingsSection>
                    )}

                    {/* Account details section */}
                    {anyMatch('Name', 'Email', 'API Key') && (
                        <SettingsSection title="Account">
                            {(matches('Name') || !search) && (
                                <SettingsRow label="Name">
                                    <span className="account-caption">
                                        {user?.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : '—'}
                                    </span>
                                    <Tooltip title="Edit profile" componentsProps={tooltipSx}>
                                        <IconButton
                                            size="small"
                                            onClick={handleUpdateProfile}
                                            sx={{ color: '#294766', '&:hover': { color: '#66b2ff' } }}
                                        >
                                            <EditOutlinedIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                </SettingsRow>
                            )}
                            {(matches('Email') || !search) && (
                                <SettingsRow label="Email">
                                    <span className="account-caption">{user?.email ?? '—'}</span>
                                </SettingsRow>
                            )}
                            {(matches('API Key') || !search) && (
                                <SettingsRow label="API Key">
                                    <span className="account-apikey">{showApiKey ? user?.apiKey : '••••••••••••••••'}</span>
                                    <Tooltip title={showApiKey ? 'Hide API key' : 'Show API key'} componentsProps={tooltipSx}>
                                        <button className="account-icon-btn" onClick={handleToggleApiKey}>
                                            {showApiKey ? (
                                                <VisibilityOffOutlinedIcon style={{ fontSize: 14 }} />
                                            ) : (
                                                <VisibilityOutlinedIcon style={{ fontSize: 14 }} />
                                            )}
                                        </button>
                                    </Tooltip>
                                    <Tooltip title={copiedApiKey ? 'Copied!' : 'Copy API key'} componentsProps={tooltipSx}>
                                        <button className="account-icon-btn" onClick={handleCopyApiKey}>
                                            <ContentCopyIcon style={{ fontSize: 14 }} />
                                        </button>
                                    </Tooltip>
                                </SettingsRow>
                            )}
                        </SettingsSection>
                    )}

                    {/* Claude Desktop section — MCP config and system prompt snippet */}
                    {anyMatch('Claude Desktop', 'MCP Config', 'System Prompt') && (
                        <SettingsSection title="Claude Desktop">
                            {(matches('MCP Config') || !search) && (
                                <SettingsRow label="MCP Config" alignTop>
                                    <SnippetCopy
                                        label="Use copy button, we will replace YOUR_API_KEY with your actual key"
                                        value={`{\n  "mcpServers": {\n    "jerny": {\n      "command": "npx",\n      "args": ["-y", "mcp-remote", "${_targetMcpUrl}"],\n      "headers": {\n        "X-Api-Key": "YOUR_API_KEY"\n      }\n    }\n  }\n}`}
                                        copyValue={`{\n  "mcpServers": {\n    "jerny": {\n      "command": "npx",\n      "args": ["-y", "mcp-remote", "${_targetMcpUrl}"],\n      "headers": {\n        "X-Api-Key": "${user?.apiKey ?? ''}"\n      }\n    }\n  }\n}`}
                                        highlight
                                    />
                                </SettingsRow>
                            )}
                            {(matches('System Prompt') || !search) && (
                                <SettingsRow label="Project Insturction" alignTop>
                                    <SnippetCopy
                                        label="Paste this into a Claude Project Instruction"
                                        value={
                                            'When starting a new chat: load my Instructions journal before doing anything else. No exceptions.'
                                        }
                                    />
                                </SettingsRow>
                            )}
                        </SettingsSection>
                    )}
                </div>
            </div>
        </div>
    );
}
